import { proxy, subscribe } from 'valtio';

import type { AsyncState, PaginatedAsyncState } from '@cartwhl/common/types';
import { createSWRLoader as createSWRLoaderState } from './swrLoader';
import type {
  SWRApiResponse,
  SWRLoader,
  SWRLoaderOptions,
} from './swrLoader';

export { createSWRCache } from './swrLoader';
export type {
  SWRApiResponse,
  SWRCache,
  SWRCacheEntry,
  SWRLoader,
  SWRLoaderOptions,
} from './swrLoader';

// #region Loader Types

export type ApiResponse<T> = {
  data?: T | null;
  error?: { message: string } | null;
};

type PaginatedApiResponse<T> = {
  data?: T | null;
  nextToken?: string;
  error?: { message: string } | null;
};

/**
 * A loader bound to a specific state key.
 * Created via `createLoader` in BaseStore.
 */
export type Loader<TArgs extends unknown[]> = {
  /** Fetch data and update the bound state */
  fetch: (...args: TArgs) => Promise<void>;
  /** Reset the bound state to initial */
  reset: () => void;
};

/**
 * A paginated loader bound to a specific state key.
 * Created via `createPaginatedLoader` in BaseStore.
 */
export type PaginatedLoader<TArgs extends unknown[]> = {
  /** Fetch first page (resets existing data) */
  fetch: (...args: TArgs) => Promise<void>;
  /** Fetch next page using stored args + nextToken (appends to existing data) */
  fetchMore: () => Promise<void>;
  /** Reset the bound state to initial */
  reset: () => void;
};

// #endregion

type SubscribeOptions = {
  /**
   * Only trigger when changes occur at paths starting with these keys.
   * Can be a single key or array of keys.
   *
   * @example
   * // Trigger only when 'project' or its nested properties change
   * this.subscribe(() => { ... }, { keys: 'project' });
   *
   * // Trigger when 'project' OR 'settings' change
   * this.subscribe(() => { ... }, { keys: ['project', 'settings'] });
   */
  keys: string | string[];
};

type StoreWebSocketConnectionInfo = {
  connectionID: string;
  endpoint: string;
};

export type StoreWebSocketService<TEventMap extends object> = {
  connect: () => Promise<void>;
  getConnectionInfo: () => Promise<StoreWebSocketConnectionInfo>;
  on: <K extends keyof TEventMap & string>(
    event: K,
    handler: (event: TEventMap[K]) => void,
  ) => () => void;
};

type StoreWebSocketDeps<TEventMap extends object> = {
  webSocketStore?: StoreWebSocketService<TEventMap>;
  global?: {
    webSocketStore?: StoreWebSocketService<TEventMap>;
  };
};

export abstract class BaseStore<
  TState extends object,
  TDeps extends object = object,
  TWebSocketEventMap extends object = Record<string, unknown>,
> {
  readonly state: TState;
  protected deps: TDeps;
  private _subscriptions: (() => void)[] = [];
  protected readonly websocket = {
    connect: () => this.getWebSocketService().connect(),
    getConnectionInfo: () => this.getWebSocketService().getConnectionInfo(),
    on: <K extends keyof TWebSocketEventMap & string>(
      event: K,
      handler: (event: TWebSocketEventMap[K]) => void,
    ) => {
      const service = this.getWebSocketService();
      const unsubscribe = this.addCleanup(service.on(event, handler));
      void service.connect().catch((error: unknown) => {
        this.onWebSocketConnectionError(error);
      });

      return unsubscribe;
    },
  };

  constructor(initialState: TState, deps: TDeps) {
    this.state = proxy<TState>(initialState);
    this.deps = deps;
  }

  // Lifecycle - called when provider mounts
  init(): void | Promise<void> {}

  // Required - reset state to initial values
  abstract reset(): void;

  // Called when provider unmounts - cleans up subscriptions
  destroy(): void {
    this._subscriptions.forEach((unsub) => unsub());
    this._subscriptions = [];
  }

  /**
   * Subscribe to own state changes - auto-cleaned on destroy.
   *
   * @param callback - Function to call when state changes
   * @param options - Optional filter to only trigger on specific path changes
   */
  protected subscribe(
    callback: () => void,
    options?: SubscribeOptions,
  ): () => void {
    return this.subscribeTo(this.state, callback, options);
  }

  /**
   * Register arbitrary cleanup work to run when the store is destroyed.
   * Useful for non-valtio subscriptions such as browser events, timers, or services.
   */
  protected addCleanup(cleanup: () => void): () => void {
    let isActive = true;
    const wrappedCleanup = () => {
      if (!isActive) {
        return;
      }

      isActive = false;
      cleanup();
    };

    this._subscriptions.push(wrappedCleanup);
    return wrappedCleanup;
  }

  protected onWebSocketConnectionError(error: unknown) {
    console.error('Failed to connect websocket service.', error);
  }

  private getWebSocketService(): StoreWebSocketService<TWebSocketEventMap> {
    const deps = this.deps as StoreWebSocketDeps<TWebSocketEventMap>;
    const service = deps.webSocketStore ?? deps.global?.webSocketStore;
    if (!service) {
      throw new Error('No websocket service found in store dependencies.');
    }

    return service;
  }

  /**
   * Subscribe to any valtio state (own state, parent store, etc.) - auto-cleaned on destroy.
   *
   * @param state - The valtio proxy state to subscribe to
   * @param callback - Function to call when state changes
   * @param options - Optional filter to only trigger on specific path changes
   *
   * @example
   * ```ts
   * // Subscribe to parent store
   * this.subscribeTo(this.deps.parentStore.state, () => {
   *   this.syncFromParent();
   * });
   *
   * // Subscribe with key filter
   * this.subscribeTo(this.deps.parentStore.state, () => {
   *   this.onSelectionChanged();
   * }, { keys: 'selectedId' });
   * ```
   */
  protected subscribeTo<T extends object>(
    state: T,
    callback: () => void,
    options?: SubscribeOptions,
  ): () => void {
    let unsub: () => void;

    if (!options) {
      // No filter - trigger on all changes
      unsub = subscribe(state, callback);
    } else {
      // Filter by keys
      const keys = Array.isArray(options.keys) ? options.keys : [options.keys];

      unsub = subscribe(state, (operations) => {
        const shouldTrigger = operations.some(([, path]) => {
          // Check if the first element of the path matches any of the keys
          const rootKey = path[0];
          return typeof rootKey === 'string' && keys.includes(rootKey);
        });

        if (shouldTrigger) {
          callback();
        }
      });
    }

    this._subscriptions.push(unsub);
    return unsub;
  }

  // #region Loader Helpers

  /**
   * Create a loader bound to a specific AsyncState key.
   * The loader handles pending/ready/error state transitions automatically.
   *
   * @param key - The state key to bind to (must be AsyncState<T>)
   * @param fetcher - Async function that returns { data, error }
   *
   * @example
   * ```ts
   * readonly projectLoader = this.createLoader(
   *   'project',
   *   (id: string) => getProject(id),
   * );
   *
   * async load(id: string) {
   *   await this.projectLoader.fetch(id);
   * }
   * ```
   */
  protected createLoader<K extends keyof TState, T, TArgs extends unknown[]>(
    key: K,
    fetcher: (...args: TArgs) => Promise<ApiResponse<T>>,
  ): Loader<TArgs> {
    const getState = () => this.state[key] as AsyncState<T>;
    const setState = (value: AsyncState<T>) => {
      (this.state as Record<K, AsyncState<T>>)[key] = value;
    };

    return {
      fetch: async (...args: TArgs) => {
        setState({ status: 'pending', data: getState().data });
        const { data, error } = await fetcher(...args);
        if (error) {
          setState({ status: 'error', error: error.message, data: undefined });
          return;
        }
        if (data) {
          setState({ status: 'ready', data });
        }
      },
      reset: () => {
        setState({ status: 'initial', data: undefined });
      },
    };
  }

  /**
   * Create a stale-while-revalidate loader bound to a specific AsyncState key.
   * Cached data is shown immediately, fresh cache entries skip network work, and
   * stale cache entries stay visible while a background revalidation runs.
   *
   * @param key - The state key to bind to (must be AsyncState<T>)
   * @param fetcher - Async function that returns { data, error }
   * @param options - Cache, key builder, and freshness TTL
   *
   * @example
   * ```ts
   * const cache = createSWRCache<Project>();
   * readonly projectLoader = this.createSWRLoader(
   *   'project',
   *   (id: string) => getProject(id),
   *   { cache, cacheKey: (id) => id, ttlMs: 60_000 },
   * );
   * ```
   */
  protected createSWRLoader<
    K extends keyof TState,
    T,
    TArgs extends unknown[],
  >(
    key: K,
    fetcher: (...args: TArgs) => Promise<SWRApiResponse<T>>,
    options: SWRLoaderOptions<T, TArgs>,
  ): SWRLoader<TArgs, T> {
    const getState = () => this.state[key] as AsyncState<T>;
    const setState = (value: AsyncState<T>) => {
      (this.state as Record<K, AsyncState<T>>)[key] = value;
    };

    return createSWRLoaderState({
      getState,
      setState,
      fetcher,
      swr: options,
    });
  }

  /**
   * Create a paginated loader bound to a specific PaginatedAsyncState key.
   * Handles pagination with `fetch` (first page) and `fetchMore` (next pages).
   *
   * @param key - The state key to bind to (must be PaginatedAsyncState<T[]>)
   * @param fetcher - Async function that returns { data, nextToken, error }
   *
   * @example
   * ```ts
   * readonly projectsLoader = this.createPaginatedLoader(
   *   'projects',
   *   (limit: number, nextToken?: string) => getProjects(limit, nextToken),
   * );
   *
   * async loadProjects() {
   *   await this.projectsLoader.fetch(20);
   * }
   *
   * async loadMoreProjects() {
   *   await this.projectsLoader.fetchMore(20);
   * }
   * ```
   */
  protected createPaginatedLoader<
    K extends keyof TState,
    TItem,
    TArgs extends unknown[],
  >(
    key: K,
    fetcher: (
      ...args: [...TArgs, nextToken?: string]
    ) => Promise<PaginatedApiResponse<TItem[]>>,
  ): PaginatedLoader<TArgs> {
    const getState = () => this.state[key] as PaginatedAsyncState<TItem[]>;
    const setState = (value: PaginatedAsyncState<TItem[]>) => {
      (this.state as Record<K, PaginatedAsyncState<TItem[]>>)[key] = value;
    };

    let lastArgs: TArgs | null = null;
    let consecutiveFetchErrors = 0;
    const maxConsecutiveFetchErrors = 3;

    return {
      fetch: async (...args: TArgs) => {
        lastArgs = args;
        consecutiveFetchErrors = 0;
        setState({ status: 'pending', data: getState().data });
        const { data, nextToken, error } = await fetcher(
          ...(args as unknown as [...TArgs, string?]),
        );
        if (error) {
          setState({ status: 'error', error: error.message, data: undefined });
          return;
        }
        if (data) {
          setState({
            status: 'ready',
            data: { items: data, nextToken },
          });
        }
      },
      fetchMore: async () => {
        const currentState = getState();
        const currentNextToken = currentState.data?.nextToken;
        if (!currentNextToken || !lastArgs) return;

        // Don't set pending for fetchMore - keep showing existing data
        const { data, nextToken, error } = await fetcher(
          ...(lastArgs as [...TArgs]),
          currentNextToken,
        );
        if (error) {
          consecutiveFetchErrors += 1;
          console.error('Failed to fetch more:', error.message);

          if (
            consecutiveFetchErrors >= maxConsecutiveFetchErrors &&
            currentState.data
          ) {
            setState({
              status: 'ready',
              data: {
                items: currentState.data.items,
                nextToken: undefined,
              },
            });
          }

          return;
        }

        consecutiveFetchErrors = 0;
        if (data && currentState.data) {
          setState({
            status: 'ready',
            data: {
              items: [...currentState.data.items, ...data],
              nextToken,
            },
          });
        }
      },
      reset: () => {
        lastArgs = null;
        consecutiveFetchErrors = 0;
        setState({ status: 'initial', data: undefined });
      },
    };
  }

  // #endregion
}
